// src/components/PackageCard.jsx
import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Star, 
  Clock, 
  TrendingUp, 
  Shield, 
  Zap,
  ArrowRight,
  Check,
  Crown,
  Target,
  DollarSign,
  Calendar
} from 'lucide-react'

const PackageCard = ({ pkg, index = 0 }) => {
  const getPackageColor = (level) => {
    switch (level) {
      case 'Level 5': return 'from-orange-500 to-pink-500'
      case 'Level 4': return 'from-purple-500 to-pink-500'
      case 'Level 3': return 'from-purple-500 to-red-500'
      case 'Level 2': return 'from-blue-500 to-cyan-500'
      case 'Starter': return 'from-cyan-500 to-lime-500'
      default: return 'from-gray-500 to-gray-600'
    }
  }

  const getPackageIcon = (level) => {
    switch (level) {
      case 'Level 5': return Shield
      case 'Level 3': return Crown
      case 'Level 4': return Crown
      case 'Level 2': return TrendingUp
      case 'Starter': return Star
      default: return Zap
    }
  }

  const getLevelColor = (level) => {
    const colors = {
      PREMIUM: 'bg-gradient-to-r from-purple-500 to-pink-500',
      ADVANCED: 'bg-gradient-to-r from-blue-500 to-cyan-500',
      BEGINNER: 'bg-gradient-to-r from-green-500 to-emerald-500'
    }
    return colors[level] || 'bg-gradient-to-r from-gray-500 to-gray-600'
  }

  const Icon = getPackageIcon(pkg.level)

  const features = pkg.features || [
    'Capital Protection',
    'Instant Withdrawal',
    '24/7 Support',
    'Compounding Available',
    'Risk Management',
    'Auto Reinvest'
  ]

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="group"
    >
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 group-hover:scale-105 overflow-hidden h-full flex flex-col">
        {/* Header with Gradient */}
        <div className={`bg-gradient-to-r ${getPackageColor(pkg.level)} p-6 text-white relative overflow-hidden`}>
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -mr-16 -mt-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full -ml-12 -mb-12"></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-3">
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{pkg.name}</h3>
                  <span className={`${getLevelColor(pkg.level)} text-white px-3 py-1 rounded-full text-xs font-bold inline-block mt-1`}>
                    {pkg.level}
                  </span>
                </div>
              </div>
            </div>

            {/* ROI and Term */}
            <div className="grid grid-cols-2 gap-4">
              
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-center">
                <div className="text-2xl font-bold mb-1">${pkg.investment || 0}</div>
                <div className="text-white/80 text-sm">Investment</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-center">
                <div className="text-2xl font-bold mb-1">{pkg.displayMinROI + "-" + pkg.displayMaxROI || 1.5-2.5}%</div>
                <div className="text-white/80 text-sm">Daily ROI</div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 flex flex-col">
          {/* Investment Range */}
          {/* <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <DollarSign className="w-4 h-4 text-gray-400" />
                <div className="text-sm text-gray-600 dark:text-gray-400">Min Investment</div>
              </div>
              <div className="text-lg font-bold text-gray-900 dark:text-white">
                ${(pkg.minInvestment || 100).toLocaleString()}
              </div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <Target className="w-4 h-4 text-gray-400" />
                <div className="text-sm text-gray-600 dark:text-gray-400">Max Investment</div>
              </div>
              <div className="text-lg font-bold text-gray-900 dark:text-white">
                ${pkg.maxInvestment ? pkg.maxInvestment.toLocaleString() : 'Unlimited'}
              </div>
            </div>
          </div> */}

          {/* Key Features */}
          <div className="space-y-3 mb-6 flex-1">
            <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-3 flex items-center space-x-2">
              <Check className="w-4 h-4 text-green-500" />
              <span>Package Features</span>
            </h4>
            {features.slice(0, 4).map((feature, idx) => (
              <div key={idx} className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">{feature}</span>
              </div>
            ))}
          </div>

          {/* Additional Info */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-4 mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="text-center">
                <div className="text-gray-600 dark:text-gray-400 mb-1">Total Returns Approx</div>
                <div className="font-bold text-gray-900 dark:text-white">
                  ${((pkg.investment || 1) * (pkg.returnCap || 3))}
                </div>
              </div>
              <div className="text-center">
                <div className="text-gray-600 dark:text-gray-400 mb-1">Minimun Withdrawal</div>
                <div className="font-bold text-green-600 dark:text-green-400">
                  ${pkg.minWithdrawal || 10}
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <Link
            to="https://udash.yestimeearn.com/register"
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-4 px-6 rounded-xl transition-all transform hover:scale-105 flex items-center justify-center space-x-2 group/btn mt-auto"
          >
            <span>Start Investing Now </span>
            <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Popular Badge for Premium Packages */}
        {/* {pkg.level === 'PREMIUM' && (
          <div className="absolute top-4 right-4 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-xs font-bold rotate-12">
            ⚡ Most Popular
          </div>
        )} */}

        {/* Hover Effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
      </div>
    </motion.div>
  )
}

export default PackageCard